import analytics from '../analytics';
import nonCriticalException from '../exceptionLogger';
import { APPSFLYER_DEBUG } from '../../config';
import store from '../../store';
import Deferred from '../../testUtilities/deferred';
import { DeepLinkType } from './constants';
import {
  PRO_TAPTOPAY_ONBOARD_SETTINGS_DEEP_LINK,
  CHAT,
  CLIENT_APPTS_LIST_DEFAULT,
  PROVIDER_GROWTH_REPORTING,
  PROVIDER_GROWTH_SUMMARY,
  PROFILE_ROUTE,
  PRO_SETTINGS_PLAN_SELECTION_DEEP_LINK,
  PROVIDER_SETTINGS_ONLINE_BOOKING_DEEP_LINK,
  CLIENT_APPT_STATUS_DETAILS,
  PRO_CALENDAR_VIEW_APPOINTMENT,
} from '../../route-names';

export type DeeplinkResponse = {
  type: string;
  status: string;
  data: {
    deep_link_value: string;
    deep_link_sub1: any;
    deep_link_sub2?: any;
    install_time: string;
    af_dp: string;
    path: string;
    scheme: string;
    host: string;
    pid: string;
  };
};

const APPSFLYER_DEV_KEY = 'qnHjYCy8kR2xWVQ3hVsR9d';
const APPSFLYER_APP_ID = '414313281';

// https://support.appsflyer.com/hc/en-us/articles/360014381317-SparkPost-integration-with-AppsFlyer
const resolveDeepLinkURLs = [
  'link.styleseat.com',
  'link.styleseat.works',
  'spgo.io',
];

const trackInstallAttribution: (data: string) => Promise<void> = async data => {
  try {
    const conversionData = JSON.parse(data)?.data;
    if (conversionData?.is_first_launch) {
      const segmentTracker = await analytics.getTracker('segment');
      const properties = {
        ...conversionData,
        utm_source: conversionData.media_source,
        utm_campaign: conversionData.campaign,
        provider: 'AppsFlyer',
      };
      segmentTracker.trackEvent('Install Attributed', properties);
    }
  } catch (e) {
    nonCriticalException(e);
  }
};

export const readyForDeepLinking = new Deferred();

export const markDeepLinkingReady = () => {
  if (!readyForDeepLinking.isFulfilled) {
    readyForDeepLinking.resolve();
  }
};

export const init = async () => {
  window?.plugins?.appsFlyer?.registerDeepLink(async res => {
    try {
      await readyForDeepLinking.promise;
      const { dispatch, getState } = store;
      const deeplinkData: DeeplinkResponse = JSON.parse(res);
      const {
        deep_link_value: access_type,
        deep_link_sub1: access_value,
        deep_link_sub2: access_value_2,
      } = deeplinkData.data;
      // redirect here
      if (access_type === DeepLinkType.ProviderId) {
        await dispatch.route.go({
          route: PROFILE_ROUTE,
          params: { providerId: access_value },
        });
      } else if (access_type === DeepLinkType.TapToPaySetup) {
        await dispatch.route.go({ route: PRO_TAPTOPAY_ONBOARD_SETTINGS_DEEP_LINK });
      } else if (access_type === DeepLinkType.ChatId) {
        // tracking for deeplinking;
        if (access_value_2 === 'provider') {
          const providerId = getState().user?.providerId;
          analytics.track('chat_page_from_deeplinking_viewed', {
            provider_id: providerId,
            required_role: access_value_2,
            chat_id: access_value,
          });
        }
        if (access_value_2 === 'client') {
          const userId = getState().user?.userId;
          analytics.track('chat_page_from_deeplinking_viewed', {
            client_user_id: userId,
            required_role: access_value_2,
            chat_id: access_value,
          });
        }
        await dispatch.route.go({
          route: CHAT,
          params: {
            chatId: access_value,
            requiredRole: access_value_2,
          },
        });
      } else if (access_type === DeepLinkType.AppointmentList) {
        await dispatch.route.go({ route: CLIENT_APPTS_LIST_DEFAULT });
      } else if (access_type === DeepLinkType.ProviderStats) {
        await dispatch.route.go(
          {
            route: PROVIDER_GROWTH_REPORTING,
            params: { providerId: access_value },
          },
        );
      } else if (access_type === DeepLinkType.ProviderGrowthTabSummary) {
        await dispatch.route.go(
          {
            route: PROVIDER_GROWTH_SUMMARY,
            params: { providerId: access_value },
          },
        );
      } else if (access_type === DeepLinkType.SearchPage) {
        await dispatch.route.go({
          route: 'search',
          params: {
            q: access_value,
          },
        });
      } else if (access_type === DeepLinkType.ProviderSettingsPlanSelection) {
        await dispatch.route.go({ route: PRO_SETTINGS_PLAN_SELECTION_DEEP_LINK });
      } else if (access_type === DeepLinkType.ProviderSettingsOnlineBooking) {
        await dispatch.route.go({ route: PROVIDER_SETTINGS_ONLINE_BOOKING_DEEP_LINK });
      } else if (access_type === DeepLinkType.ClientIntakeForm) {
        await dispatch.route.go({
          route: CLIENT_APPT_STATUS_DETAILS,
          params: {
            id: access_value, // appointment id
            showIntakeForm: true,
          },
        });
      } else if (access_type === DeepLinkType.ProviderViewIntakeForm) {
        await dispatch.route.go({
          route: PRO_CALENDAR_VIEW_APPOINTMENT,
          params: {
            providerId: access_value,
            viewApptId: access_value_2,
            showIntakeFormResponse: true,
          },
        });
      } else {
        nonCriticalException('onAppOpenAttribution access type error');
      }
    } catch (e) {
      nonCriticalException(e);
    }
  });

  window?.plugins?.appsFlyer?.setResolveDeepLinkURLs(resolveDeepLinkURLs);

  window?.plugins?.appsFlyer?.initSdk?.(
    {
      devKey: APPSFLYER_DEV_KEY,
      isDebug: APPSFLYER_DEBUG,
      appId: APPSFLYER_APP_ID,
      waitForATTUserAuthorization: 10, // time for the sdk to wait before launch - IOS 14 ONLY!
      onInstallConversionDataListener: true,
      onDeepLinkListener: true,
    },
    trackInstallAttribution, // success callback
    nonCriticalException, // error callback
  );
};
